import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAuditLogs, type AuditLogFilters } from '../api/audit-logs-api';
import { getUsers } from '@/features/users/api/users-api';
import { useDataCache, makePaginatedCacheKey } from '@/store/data-cache-context';
import { useOrg } from '@/store/org-context';
import type { User } from '@/types/api';

const PER_PAGE = 15;
const USERS_PER_PAGE_FOR_FILTER = 100;

export function useAuditLogsPage() {
  const { getAuditLogsPage, setAuditLogsPage, getUsersPage, setUsersPage } = useDataCache();
  const { activeOrgId } = useOrg();

  const [searchParams, setSearchParams] = useSearchParams();
  const resourceType = searchParams.get('resourceType') || '';
  const userId = searchParams.get('userId') || '';
  const from = searchParams.get('from') || '';
  const to = searchParams.get('to') || '';
  const page = Number(searchParams.get('page') || '1');

  const filters: AuditLogFilters = {
    resourceType: resourceType || undefined,
    userId: userId || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    perPage: PER_PAGE,
  };

  const cacheKey = makePaginatedCacheKey({ resourceType, userId, from, to, page, perPage: PER_PAGE });
  const cached = getAuditLogsPage(cacheKey);

  // Filter dropdown options: org-scoped user list (reuse the existing users cache).
  const usersCacheKey = makePaginatedCacheKey({ org: activeOrgId, perPage: USERS_PER_PAGE_FOR_FILTER });
  const cachedUsers = getUsersPage(usersCacheKey);
  const [users, setUsers] = useState<User[]>(cachedUsers?.items ?? []);

  useEffect(() => {
    if (getAuditLogsPage(cacheKey)) return;
    loadLogs();
  }, [cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeOrgId) return;
    const existing = getUsersPage(usersCacheKey);
    if (existing) {
      setUsers(existing.items);
      return;
    }
    loadUsers();
  }, [usersCacheKey, activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadLogs() {
    try {
      const response = await getAuditLogs(filters);
      setAuditLogsPage(cacheKey, response);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    }
  }

  async function loadUsers() {
    try {
      const response = await getUsers({ perPage: USERS_PER_PAGE_FOR_FILTER, orgId: activeOrgId });
      setUsersPage(usersCacheKey, response);
      setUsers(response.items);
    } catch (error) {
      console.error('Failed to load users for audit log filter:', error);
    }
  }

  function handleFilterChange(updates: Partial<AuditLogFilters>) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (updates.resourceType !== undefined) {
          if (updates.resourceType) next.set('resourceType', updates.resourceType);
          else next.delete('resourceType');
        }
        if (updates.userId !== undefined) {
          if (updates.userId) next.set('userId', updates.userId);
          else next.delete('userId');
        }
        if (updates.from !== undefined) {
          if (updates.from) next.set('from', updates.from);
          else next.delete('from');
        }
        if (updates.to !== undefined) {
          if (updates.to) next.set('to', updates.to);
          else next.delete('to');
        }
        next.delete('page'); // reset to page 1 on filter change
        return next;
      },
      { replace: true }
    );
  }

  function handlePageChange(newPage: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newPage > 1) next.set('page', String(newPage));
        else next.delete('page');
        return next;
      },
      { replace: true }
    );
  }

  const logs = cached?.items ?? [];
  const totalCount = cached?.totalCount ?? 0;
  const totalPages = cached?.totalPages ?? 1;
  const loading = !cached;

  return {
    logs,
    loading,
    totalCount,
    totalPages,
    filters,
    users,
    handleFilterChange,
    handlePageChange,
  };
}

export type AuditLogsPageState = ReturnType<typeof useAuditLogsPage>;
